import React, { useEffect, useMemo, useRef, useState } from "react";

const VERSION = "v0.11.0";

/**
 * v0.11.0
 * - Two-tier polling:
 *    - FAST loop: RPM (010C) + Speed (010D) frequently (rapid scrape)
 *    - SLOW loop: all other supported PIDs slower
 * - Real Mode 01 supported PID discovery:
 *    queries 0100/0120/0140/0160/0180 and parses the bitmasks
 * - Larger PID catalog (common Mode 01 PIDs). Only supported ones are polled.
 * - Light theme retained + 270° gauges
 */

export default function OBD2Reader() {
  const [status, setStatus] = useState("idle");
  const [deviceName, setDeviceName] = useState(null);
  const [transportLabel, setTransportLabel] = useState("not selected yet");
  const [protocolLabel, setProtocolLabel] = useState("unknown");
  const [logLines, setLogLines] = useState([]);

  const [scanSecondsLeft, setScanSecondsLeft] = useState(0);

  const [supportedMode01Pids, setSupportedMode01Pids] = useState([]);
  const [goodByPid, setGoodByPid] = useState({});

  const [parsed, setParsed] = useState({
    rpm: null,
    speedMph: null,
    coolantF: null,
    intakeF: null,
    throttlePct: null,
    engineLoadPct: null,
    fuelPct: null,
    mapKpa: null,
    timingAdvanceDeg: null,

    mafGps: null,
    stft1Pct: null,
    ltft1Pct: null,
    stft2Pct: null,
    ltft2Pct: null,
    baroKpa: null,
    ambientF: null,
    runTimeSec: null,
  });

  const [stats, setStats] = useState({
    lastRequest: "",
    lastResponseAt: null,
    responses: 0,
    timeouts: 0,
  });

  const bluetoothDeviceRef = useRef(null);
  const txCharacteristicRef = useRef(null);
  const rxCharacteristicRef = useRef(null);

  const connectingRef = useRef(false);

  const stopPollingRef = useRef(false);
  const pollingFastRef = useRef(null);
  const pollingSlowRef = useRef(null);

  const rxTextBufferRef = useRef("");
  const waitersRef = useRef([]);

  // serialize commands from multiple loops so they don't collide
  const cmdLockRef = useRef(Promise.resolve());

  // ---------- Light theme tokens ----------
  const UI = useMemo(
    () => ({
      pageBg: "#ffffff",
      text: "#111827",
      subtleText: "#374151",
      border: "#111827",
      borderSoft: "rgba(17,24,39,0.18)",
      panelBg: "#ffffff",
      panelBg2: "#f9fafb",
      buttonBg: "#ffffff",
      buttonBorder: "#111827",
    }),
    []
  );

  const pushLog = (msg) => {
    const line = `${new Date().toLocaleTimeString()}: ${msg}`;
    setLogLines((prev) => [...prev, line].slice(-900));
    console.log(msg);
  };

  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  const OBD_OPTIONAL_SERVICES = useMemo(
    () => [
      "0000ffe0-0000-1000-8000-00805f9b34fb",
      "0000fff0-0000-1000-8000-00805f9b34fb",
      "0000ae00-0000-1000-8000-00805f9b34fb",
      "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
      "00001800-0000-1000-8000-00805f9b34fb",
      "00001801-0000-1000-8000-00805f9b34fb",
    ],
    []
  );

  const isConnected = () => !!bluetoothDeviceRef.current?.gatt?.connected;

  const cleanupConnection = () => {
    stopPollingRef.current = true;

    const ws = waitersRef.current;
    waitersRef.current = [];
    for (const w of ws) {
      clearTimeout(w.timeoutId);
      try {
        w.reject?.(new Error("Disconnected"));
      } catch {}
    }

    try {
      rxCharacteristicRef.current?.removeEventListener(
        "characteristicvaluechanged",
        handleNotifications
      );
    } catch {}

    txCharacteristicRef.current = null;
    rxCharacteristicRef.current = null;

    pollingFastRef.current = null;
    pollingSlowRef.current = null;
  };

  const handleDisconnect = () => {
    pushLog("🔌 Device disconnected.");
    setStatus("disconnected");
    cleanupConnection();
  };

  const handleNotifications = (e) => {
    const chunk = new TextDecoder().decode(
      new Uint8Array(e.target.value.buffer)
    );
    if (!chunk) return;

    rxTextBufferRef.current += chunk;

    // normalize: adapter uses '>' prompt; also normalize newlines
    const normalized = rxTextBufferRef.current
      .replace(/\n/g, "\r")
      .replace(/>/g, "\r>\r");

    const parts = normalized.split("\r");
    rxTextBufferRef.current = parts.pop() ?? "";

    for (const rawLine of parts) {
      const line = rawLine.trim();
      if (!line) continue;

      setStats((s) => ({
        ...s,
        responses: s.responses + (line === ">" ? 0 : 1),
        lastResponseAt: Date.now(),
      }));

      pushLog(`📨 RAW: ${line}`);
      parseOBDResponse(line);

      const waiters = waitersRef.current.slice();
      for (const w of waiters) {
        try {
          if (w.predicate(line)) {
            clearTimeout(w.timeoutId);
            waitersRef.current = waitersRef.current.filter((x) => x !== w);
            w.resolve(line);
          }
        } catch {}
      }
    }
  };

  const awaitResponse = (predicate, timeoutMs = 2000) =>
    new Promise((resolve, reject) => {
      const w = {
        predicate,
        resolve,
        reject,
        timeoutId: setTimeout(() => {
          waitersRef.current = waitersRef.current.filter((x) => x !== w);
          setStats((s) => ({ ...s, timeouts: s.timeouts + 1 }));
          reject(new Error("Response timeout"));
        }, timeoutMs),
      };
      waitersRef.current.push(w);
    });

  // serialize a function so only one OBD command is in-flight at a time
  const withCmdLock = async (fn) => {
    const prev = cmdLockRef.current;
    let resolveNext;
    const next = new Promise((r) => (resolveNext = r));
    cmdLockRef.current = prev.then(() => next);

    await prev;
    try {
      return await fn();
    } finally {
      resolveNext();
    }
  };

  const sendRaw = async (
    cmd,
    { silent = false, writeCharOverride = null } = {}
  ) => {
    const tx = writeCharOverride || txCharacteristicRef.current;
    if (!tx) {
      if (!silent) pushLog("⚠️ No writable characteristic available.");
      throw new Error("No writable characteristic");
    }

    const encoder = new TextEncoder();
    const payload = encoder.encode(cmd + "\r");

    try {
      if (tx.properties?.writeWithoutResponse && !tx.properties?.write) {
        await tx.writeValueWithoutResponse(payload);
      } else {
        await tx.writeValue(payload);
      }
      if (!silent) pushLog(`➡️ Sent: ${cmd}`);
      setStats((s) => ({ ...s, lastRequest: cmd }));
    } catch (err) {
      if (!silent) pushLog("❗ Send error: " + (err?.message || String(err)));
      throw err;
    }
  };

  const isHexLike = (s) => /^[0-9A-F ]{2,}$/i.test(s);

  const toF = (c) => (c * 9) / 5 + 32;
  const mph = (kmh) => kmh * 0.621371;

  const parseMode01 = (pid, bytes) => {
    const b = bytes;

    switch (pid) {
      case 0x0c: {
        if (b.length < 2) return null;
        const rpmVal = (b[0] * 256 + b[1]) / 4;
        return { key: "rpm", value: rpmVal };
      }
      case 0x0d: {
        if (b.length < 1) return null;
        return { key: "speedMph", value: mph(b[0]) };
      }
      case 0x05: {
        if (b.length < 1) return null;
        return { key: "coolantF", value: toF(b[0] - 40) };
      }
      case 0x0f: {
        if (b.length < 1) return null;
        return { key: "intakeF", value: toF(b[0] - 40) };
      }
      case 0x11: {
        if (b.length < 1) return null;
        return { key: "throttlePct", value: (b[0] * 100) / 255 };
      }
      case 0x04: {
        if (b.length < 1) return null;
        return { key: "engineLoadPct", value: (b[0] * 100) / 255 };
      }
      case 0x2f: {
        if (b.length < 1) return null;
        return { key: "fuelPct", value: (b[0] * 100) / 255 };
      }
      case 0x0b: {
        if (b.length < 1) return null;
        return { key: "mapKpa", value: b[0] };
      }
      case 0x0e: {
        if (b.length < 1) return null;
        return { key: "timingAdvanceDeg", value: b[0] / 2 - 64 };
      }

      // extras
      case 0x10: {
        // MAF: ((A*256)+B) / 100 g/s
        if (b.length < 2) return null;
        return { key: "mafGps", value: (b[0] * 256 + b[1]) / 100 };
      }
      case 0x06: {
        // STFT B1: (A-128) * 100/128
        if (b.length < 1) return null;
        return { key: "stft1Pct", value: ((b[0] - 128) * 100) / 128 };
      }
      case 0x07: {
        // LTFT B1
        if (b.length < 1) return null;
        return { key: "ltft1Pct", value: ((b[0] - 128) * 100) / 128 };
      }
      case 0x08: {
        // STFT B2
        if (b.length < 1) return null;
        return { key: "stft2Pct", value: ((b[0] - 128) * 100) / 128 };
      }
      case 0x09: {
        // LTFT B2
        if (b.length < 1) return null;
        return { key: "ltft2Pct", value: ((b[0] - 128) * 100) / 128 };
      }
      case 0x33: {
        // BARO
        if (b.length < 1) return null;
        return { key: "baroKpa", value: b[0] };
      }
      case 0x46: {
        // Ambient air temp (C): A - 40
        if (b.length < 1) return null;
        return { key: "ambientF", value: toF(b[0] - 40) };
      }
      case 0x1f: {
        // Run time since engine start: A*256 + B (seconds)
        if (b.length < 2) return null;
        return { key: "runTimeSec", value: b[0] * 256 + b[1] };
      }

      default:
        return null;
    }
  };

  const parseOBDResponse = (line) => {
    const clean = line.replace(/>/g, "").trim();
    if (!clean || !isHexLike(clean)) return;

    const compact = clean.replace(/\s+/g, "");
    const bytes =
      compact.match(/.{1,2}/g)?.map((h) => parseInt(h, 16)) || [];
    if (bytes.length < 2) return;

    // Mode 01 response
    if (bytes[0] === 0x41) {
      const pid = bytes[1];
      const payload = bytes.slice(2);
      const update = parseMode01(pid, payload);
      if (update) setParsed((p) => ({ ...p, [update.key]: update.value }));
    }
  };

  const waitForPidResponse = async (pidHex, timeoutMs = 4500) => {
    const pid = pidHex.slice(2).toUpperCase();
    try {
      const line = await awaitResponse(
        (l) => {
          const compact = l.replace(/\s+/g, "").toUpperCase();
          if (compact === ">") return false;
          if (/BUS INIT|SEARCHING|CAN ERROR/i.test(l)) return true;
          if (/NO DATA|UNABLE|STOPPED/i.test(l)) return true;
          if (compact.startsWith("41" + pid)) return true;
          if (isHexLike(l) && compact.includes("41" + pid)) return true;
          return false;
        },
        timeoutMs
      );
      return line;
    } catch {
      return null;
    }
  };

  const isGoodPidLine = (pidHex, line) => {
    const compact = line.replace(/\s+/g, "").toUpperCase();
    if (!isHexLike(line)) return false;
    if (
      /NO DATA|UNABLE|STOPPED|CAN ERROR/i.test(line)
    )
      return false;
    if (!pidHex.startsWith("01")) return false;
    const pid = pidHex.slice(2).toUpperCase();
    return compact.startsWith("41" + pid);
  };

  const waitForPromptOrAnyLine = async (timeoutMs = 1500) => {
    try {
      return await awaitResponse((l) => l === ">" || l.length > 0, timeoutMs);
    } catch {
      return null;
    }
  };

  const tryTransportPair = async (writeChar, notifyChar) => {
    let listenerAttached = false;
    try {
      if (!notifyChar.properties?.notify && !notifyChar.properties?.indicate)
        return false;

      await notifyChar.startNotifications();
      notifyChar.addEventListener("characteristicvaluechanged", handleNotifications);
      listenerAttached = true;

      await sendRaw("ATI", { silent: true, writeCharOverride: writeChar }).catch(
        () => null
      );

      const line = await awaitResponse(
        (l) => /ELM|OK|ATI|V\d|>/i.test(l) || l.length > 0,
        1800
      ).catch(() => null);

      return !!line;
    } catch {
      return false;
    } finally {
      if (listenerAttached) {
        try {
          notifyChar.removeEventListener(
            "characteristicvaluechanged",
            handleNotifications
          );
        } catch {}
      }
      try {
        await notifyChar.stopNotifications?.();
      } catch {}
    }
  };

  const getAllChars = async (server) => {
    const services = await server.getPrimaryServices();
    let chars = [];
    for (const svc of services) {
      try {
        const svcChars = await svc.getCharacteristics();
        chars = chars.concat(svcChars);
      } catch {}
    }
    return { services, chars };
  };

  const discoverTransport = async (server) => {
    const TOTAL = 10;
    setScanSecondsLeft(TOTAL);
    const tick = setInterval(
      () => setScanSecondsLeft((s) => (s > 0 ? s - 1 : 0)),
      1000
    );

    try {
      const { services, chars } = await getAllChars(server);

      pushLog(
        `🔎 Services found (${services.length}): ${services
          .map((s) => s.uuid)
          .join(", ")}`
      );
      pushLog(`🔎 Safe characteristics collected: ${chars.length}`);

      const findByShort = (short) =>
        chars.find((c) => c.uuid.toLowerCase().includes(short));

      pushLog("⚡ Trying V011 fast-path transport (FFE0/FFE1/FFE2)...");
      const ffe1 = findByShort("ffe1");
      const ffe2 = findByShort("ffe2");
      if (ffe1 && ffe2) {
        const ok = await tryTransportPair(ffe2, ffe1);
        if (ok) {
          return {
            write: ffe2,
            notify: ffe1,
            label: `V011 fast-path (write ${ffe2.uuid} → notify ${ffe1.uuid})`,
          };
        }
      }

      pushLog("🧩 Falling back to generic transport scan...");
      const writable = chars.filter(
        (c) => c.properties.write || c.properties.writeWithoutResponse
      );
      const notifiable = chars.filter(
        (c) => c.properties.notify || c.properties.indicate
      );

      pushLog(
        `✍️ Writable chars: ${writable.length} | 🔔 Notifiable chars: ${notifiable.length}`
      );

      for (const w of writable) {
        for (const n of notifiable) {
          if (w === n) continue;
          const ok = await tryTransportPair(w, n);
          if (ok) return { write: w, notify: n, label: `write ${w.uuid} → notify ${n.uuid}` };
        }
      }

      return null;
    } finally {
      clearInterval(tick);
      setScanSecondsLeft(0);
    }
  };

  const sendInitSequence = async () => {
    const initCmds = ["ATZ", "ATE0", "ATS0", "ATH0", "ATAL", "ATAT1", "ATSTFF", "ATDP"];
    pushLog("⚙️ Sending ELM init sequence...");
    for (const c of initCmds) {
      await withCmdLock(async () => {
        await sendRaw(c).catch(() => null);
        await delay(220);
        await waitForPromptOrAnyLine(2200);
      });
      await delay(120);
    }
    pushLog("✅ Base initialization complete.");
  };

  const readAdapterProtocol = async () => {
    await withCmdLock(async () => {
      await sendRaw("ATDP", { silent: true }).catch(() => null);
      const line = await awaitResponse((l) => l !== ">" && l.length > 0, 1800).catch(() => null);
      if (line) setProtocolLabel(line.replace(/>/g, "").trim());
    });
  };

  // -------- Expanded PID catalog (Mode 01) --------
  const PID_CATALOG = useMemo(
    () => [
      // core gauges
      { pidHex: "010C", pid: 0x0c, name: "Engine RPM", unit: "RPM", key: "rpm" },
      { pidHex: "010D", pid: 0x0d, name: "Vehicle Speed", unit: "MPH", key: "speedMph" },
      { pidHex: "0105", pid: 0x05, name: "Coolant Temp", unit: "°F", key: "coolantF" },
      { pidHex: "010F", pid: 0x0f, name: "Intake Air Temp", unit: "°F", key: "intakeF" },
      { pidHex: "0111", pid: 0x11, name: "Throttle Position", unit: "%", key: "throttlePct" },
      { pidHex: "0104", pid: 0x04, name: "Engine Load", unit: "%", key: "engineLoadPct" },
      { pidHex: "012F", pid: 0x2f, name: "Fuel Level", unit: "%", key: "fuelPct" },
      { pidHex: "010B", pid: 0x0b, name: "MAP", unit: "kPa", key: "mapKpa" },
      { pidHex: "010E", pid: 0x0e, name: "Timing Advance", unit: "°", key: "timingAdvanceDeg" },

      // extras (commonly supported)
      { pidHex: "0110", pid: 0x10, name: "MAF Air Flow", unit: "g/s", key: "mafGps" },
      { pidHex: "0106", pid: 0x06, name: "STFT Bank 1", unit: "%", key: "stft1Pct" },
      { pidHex: "0107", pid: 0x07, name: "LTFT Bank 1", unit: "%", key: "ltft1Pct" },
      { pidHex: "0108", pid: 0x08, name: "STFT Bank 2", unit: "%", key: "stft2Pct" },
      { pidHex: "0109", pid: 0x09, name: "LTFT Bank 2", unit: "%", key: "ltft2Pct" },
      { pidHex: "0133", pid: 0x33, name: "Barometric Pressure", unit: "kPa", key: "baroKpa" },
      { pidHex: "0146", pid: 0x46, name: "Ambient Air Temp", unit: "°F", key: "ambientF" },
      { pidHex: "011F", pid: 0x1f, name: "Run Time Since Start", unit: "sec", key: "runTimeSec" },

      // more common (we may not decode yet; still useful for Good Responses list)
      { pidHex: "010A", pid: 0x0a, name: "Fuel Pressure", unit: "kPa", key: null },
      { pidHex: "0114", pid: 0x14, name: "O2 B1S1", unit: "", key: null },
      { pidHex: "0115", pid: 0x15, name: "O2 B1S2", unit: "", key: null },
      { pidHex: "011C", pid: 0x1c, name: "OBD Standards", unit: "", key: null },
      { pidHex: "0121", pid: 0x21, name: "Distance MIL On", unit: "km", key: null },
      { pidHex: "0131", pid: 0x31, name: "Distance Since Codes Cleared", unit: "km", key: null },
      { pidHex: "0142", pid: 0x42, name: "Control Module Voltage", unit: "V", key: null },
      { pidHex: "014C", pid: 0x4c, name: "Commanded Throttle Actuator", unit: "%", key: null },
    ],
    []
  );

  // ---------- Supported PID discovery via 0100/0120/0140/0160/0180 bitmasks ----------
  const parseSupportBitmap = (hexResp, basePid) => {
    // expects response like: 41 00 A1 B2 C3 D4 (no spaces ok)
    const compact = hexResp.replace(/\s+/g, "").toUpperCase();
    const bytes = compact.match(/.{1,2}/g)?.map((h) => parseInt(h, 16)) || [];
    if (bytes.length < 6) return [];

    // bytes[0]=0x41, bytes[1]=basePid, bytes[2..5] = 32-bit bitmap
    const bitmap = bytes.slice(2, 6);
    const supported = [];

    for (let i = 0; i < 32; i++) {
      const byteIndex = Math.floor(i / 8);
      const bitIndex = 7 - (i % 8); // MSB first
      const isSet = (bitmap[byteIndex] >> bitIndex) & 1;
      if (isSet) {
        const pid = basePid + i + 1; // basePid covers range basePid+1..basePid+32
        supported.push(pid);
      }
    }
    return supported;
  };

  const pullSupportedMode01Pids = async () => {
    const queries = ["0100", "0120", "0140", "0160", "0180"];
    const supportedSet = new Set();

    pushLog("🧾 Pulling Mode 01 supported PID ranges...");
    for (const q of queries) {
      await withCmdLock(async () => {
        pushLog(`🧾 Query: ${q}`);
        await sendRaw(q, { silent: false }).catch(() => null);
        const line = await waitForPidResponse(q, 6500);
        if (!line) return;

        const compact = line.replace(/\s+/g, "").toUpperCase();
        // expecting 41xx....
        if (!compact.startsWith("41")) {
          if (/NO DATA|STOPPED|UNABLE/i.test(line)) {
            pushLog(`⚠️ ${q} returned: ${line}`);
          } else {
            pushLog(`⚠️ Unexpected response format: ${line}`);
          }
          return;
        }

        const basePid = parseInt(q.slice(2), 16);
        const pids = parseSupportBitmap(compact, basePid);
        for (const pid of pids) supportedSet.add(pid);
      });

      // small pause between range queries (ISO 9141 likes breathing room)
      await delay(120);
    }

    const supportedHex = Array.from(supportedSet)
      .sort((a, b) => a - b)
      .map((pid) => "01" + pid.toString(16).toUpperCase().padStart(2, "0"));

    // Filter our catalog to those supported
    const supportedCatalog = PID_CATALOG.filter((p) => supportedHex.includes(p.pidHex));

    setSupportedMode01Pids(supportedCatalog);

    pushLog(`✅ Total Mode 01 supported PIDs found: ${supportedHex.length}`);
    return supportedCatalog;
  };

  // ---------- Two-tier polling config ----------
  const POLL = useMemo(
    () => ({
      // FAST: RPM+Speed
      fast: {
        pids: ["010C", "010D"],
        // K-line can choke if you try to do true 10Hz+; this is a safe start.
        cycleDelayMs: 120, // pause between cycles of (010C,010D)
        interPidDelayMs: 70, // pause between 010C and 010D
        timeoutMs: 2600,
      },
      // SLOW: everything else supported (excluding the fast PIDs)
      slow: {
        cycleDelayMs: 650, // pause between full slow cycles
        interPidDelayMs: 130, // pause between each PID in the slow list
        timeoutMs: 3500,
      },
    }),
    []
  );

  const startTwoTierPolling = (supportedCatalog) => {
    stopPollingRef.current = false;

    const stillConnected = () => !!bluetoothDeviceRef.current?.gatt?.connected;

    // Build lists
    const fastPids = POLL.fast.pids.slice();
    const slowPids = supportedCatalog
      .map((p) => p.pidHex)
      .filter((pidHex) => !fastPids.includes(pidHex));

    // Always keep fastPids present even if ECU support list is weird
    // (If ECU truly doesn't support one, we'll just not get "41xx" and it won't be "good".)
    pushLog(
      `🚗 Two-tier polling started: FAST(${fastPids.join(", ")}) | SLOW(${slowPids.length} PIDs)`
    );

    // FAST loop
    if (!pollingFastRef.current) {
      pollingFastRef.current = (async () => {
        try {
          while (!stopPollingRef.current && stillConnected()) {
            for (const pidHex of fastPids) {
              if (stopPollingRef.current || !stillConnected()) break;

              await withCmdLock(async () => {
                await sendRaw(pidHex, { silent: true }).catch(() => null);
                const resp = await waitForPidResponse(pidHex, POLL.fast.timeoutMs);

                if (resp && isGoodPidLine(pidHex, resp)) {
                  setGoodByPid((m) => ({ ...m, [pidHex]: resp }));
                }
              });

              await delay(POLL.fast.interPidDelayMs);
            }

            await delay(POLL.fast.cycleDelayMs);
          }
        } finally {
          pollingFastRef.current = null;
          pushLog("🛑 FAST polling loop exited.");
        }
      })();
    }

    // SLOW loop
    if (!pollingSlowRef.current) {
      pollingSlowRef.current = (async () => {
        try {
          while (!stopPollingRef.current && stillConnected()) {
            for (const pidHex of slowPids) {
              if (stopPollingRef.current || !stillConnected()) break;

              await withCmdLock(async () => {
                await sendRaw(pidHex, { silent: true }).catch(() => null);
                const resp = await waitForPidResponse(pidHex, POLL.slow.timeoutMs);

                if (resp && isGoodPidLine(pidHex, resp)) {
                  setGoodByPid((m) => ({ ...m, [pidHex]: resp }));
                }
              });

              await delay(POLL.slow.interPidDelayMs);
            }

            await delay(POLL.slow.cycleDelayMs);
          }
        } finally {
          pollingSlowRef.current = null;
          pushLog("🛑 SLOW polling loop exited.");
        }
      })();
    }
  };

  const dumpGattToLogs = async () => {
    try {
      const server = bluetoothDeviceRef.current?.gatt;
      if (!server?.connected) {
        pushLog("⚠️ Not connected; cannot dump GATT.");
        return;
      }
      pushLog("🧾 Dumping full GATT tree (services → characteristics → descriptors)...");
      const services = await server.getPrimaryServices();
      pushLog(`🔎 Services found: ${services.length}`);
      for (const svc of services) {
        let chars = [];
        try {
          chars = await svc.getCharacteristics();
        } catch {}
        pushLog(`📁 Service: ${svc.uuid} (${chars.length} chars)`);
        for (const ch of chars) {
          let desc = [];
          try {
            desc = await ch.getDescriptors();
          } catch {}
          const props = Object.entries(ch.properties || {})
            .filter(([, v]) => !!v)
            .map(([k]) => k)
            .join(", ");
          pushLog(`└─ 🔸 Char: ${ch.uuid} [${props || "no-props"}] desc=${desc.length}`);
        }
      }
      pushLog("✅ GATT tree dump complete.");
    } catch (e) {
      pushLog("❗ GATT dump failed: " + (e?.message || String(e)));
    }
  };

  const connect = async () => {
    if (connectingRef.current) {
      pushLog("⚠️ Previous connect is still in progress.");
      return;
    }

    try {
      connectingRef.current = true;

      if (!navigator.bluetooth) {
        pushLog("❌ Web Bluetooth not supported on this browser/device.");
        setStatus("error");
        return;
      }

      setStatus("scanning");
      pushLog("🔍 Scanning for BLE OBD devices...");

      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: OBD_OPTIONAL_SERVICES,
      });

      bluetoothDeviceRef.current = device;
      setDeviceName(device.name || "Unnamed");
      pushLog(`✅ Selected device: ${device.name || device.id}`);

      device.addEventListener("gattserverdisconnected", handleDisconnect);

      pushLog("🔄 (Re)connecting to GATT...");
      const server = await device.gatt.connect();
      pushLog("✅ GATT connection established.");
      setStatus("connected");

      setTransportLabel("discovering...");
      pushLog("🧭 Discovering transport (write/notify)...");
      const transport = await discoverTransport(server);
      if (!transport) throw new Error("No working write/notify transport found.");

      txCharacteristicRef.current = transport.write;
      rxCharacteristicRef.current = transport.notify;
      setTransportLabel(transport.label);

      await rxCharacteristicRef.current.startNotifications();
      rxCharacteristicRef.current.addEventListener("characteristicvaluechanged", handleNotifications);

      pushLog("📡 Notifications enabled / transport locked.");

      await sendInitSequence();
      await readAdapterProtocol();

      pushLog("✅ Verifying ECU response (0100)...");
      await withCmdLock(async () => {
        await sendRaw("0100").catch(() => null);
        await waitForPidResponse("0100", 6500);
      });

      // Discover supported PIDs, then start two-tier polling
      const supportedCatalog = await pullSupportedMode01Pids();

      // Warm-up: grab RPM once so UI updates quickly
      pushLog("🔥 Warm-up: reading RPM (010C)...");
      await withCmdLock(async () => {
        await sendRaw("010C").catch(() => null);
        const resp = await waitForPidResponse("010C", 4000);
        if (resp && isGoodPidLine("010C", resp)) {
          setGoodByPid((m) => ({ ...m, ["010C"]: resp }));
          pushLog(`✅ 010C → ${resp}`);
        }
      });

      startTwoTierPolling(supportedCatalog);

      pushLog("✅ Live stream active.");
    } catch (err) {
      pushLog("❗ Connection error: " + (err?.message || String(err)));
      setStatus("error");
      try {
        bluetoothDeviceRef.current?.gatt?.disconnect?.();
      } catch {}
      cleanupConnection();
    } finally {
      connectingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      try {
        bluetoothDeviceRef.current?.gatt?.disconnect?.();
      } catch {}
      cleanupConnection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ageSeconds = stats.lastResponseAt
    ? Math.floor((Date.now() - stats.lastResponseAt) / 1000)
    : null;

  // ---------- 270° Gauge ----------
  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180.0;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  const Gauge270 = ({
    label,
    value,
    unit,
    min = 0,
    max = 100,
    color = "#2563eb",
  }) => {
    const size = 170;
    const stroke = 14;
    const r = (size - stroke) / 2 - 8;
    const cx = size / 2;
    const cy = size / 2 + 10;

    const startA = 225;
    const endA = 495;

    const v = typeof value === "number" && Number.isFinite(value) ? value : null;
    const clamped = v === null ? null : Math.max(min, Math.min(max, v));
    const t = clamped === null ? 0 : (clamped - min) / (max - min);
    const valAngle = startA + t * (endA - startA);

    const basePath = describeArc(cx, cy, r, startA, endA);
    const valuePath = describeArc(cx, cy, r, startA, valAngle);

    return (
      <div
        style={{
          width: size,
          padding: 12,
          borderRadius: 16,
          background: UI.panelBg,
          border: `2px solid ${UI.border}`,
          boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
          color: UI.text,
        }}
      >
        <div style={{ fontSize: 12, color: UI.subtleText, marginBottom: 8 }}>
          {label}
        </div>

        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <path
            d={basePath}
            fill="none"
            stroke="rgba(17,24,39,0.18)"
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          <path
            d={valuePath}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
          />

          <text
            x="50%"
            y="56%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: 28, fontWeight: 900, fill: UI.text }}
          >
            {v === null ? "—" : v.toFixed(0)}
          </text>
          <text
            x="50%"
            y="68%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: 12, fill: UI.subtleText }}
          >
            {unit}
          </text>

          <text
            x="18%"
            y="88%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: 11, fill: UI.subtleText }}
          >
            {min}
          </text>
          <text
            x="82%"
            y="88%"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ fontSize: 11, fill: UI.subtleText }}
          >
            {max}
          </text>
        </svg>
      </div>
    );
  };

  const gaugeTheme = {
    speedMph: { label: "Speed", unit: "mph", min: 0, max: 120, color: "#16a34a" },
    rpm: { label: "RPM", unit: "rpm", min: 0, max: 7000, color: "#ea580c" },
    coolantF: { label: "Coolant", unit: "°F", min: -20, max: 260, color: "#0284c7" },
    intakeF: { label: "Intake", unit: "°F", min: -20, max: 200, color: "#2563eb" },
    throttlePct: { label: "Throttle", unit: "%", min: 0, max: 100, color: "#7c3aed" },
    engineLoadPct: { label: "Engine Load", unit: "%", min: 0, max: 100, color: "#ca8a04" },
    fuelPct: { label: "Fuel", unit: "%", min: 0, max: 100, color: "#059669" },
    mapKpa: { label: "MAP", unit: "kPa", min: 0, max: 255, color: "#e11d48" },
  };

  return (
    <div
      style={{
        padding: 16,
        fontFamily: "system-ui",
        minHeight: "100vh",
        background: UI.pageBg,
        color: UI.text,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0, fontWeight: 900, color: UI.text }}>
          OBD-II BLE Reader {VERSION}
        </h2>
        <div style={{ fontSize: 12, color: UI.subtleText }}>
          Status: <strong style={{ color: UI.text }}>{status}</strong>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 14, color: UI.text }}>
        <div>
          Device: <strong>{deviceName || "None"}</strong>
        </div>
        <div>
          Protocol (from adapter): <strong>{protocolLabel}</strong>
        </div>
        <div>
          Transport: <strong>{transportLabel}</strong>
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: UI.subtleText }}>
          Last request: <strong style={{ color: UI.text }}>{stats.lastRequest || "—"}</strong>
          {" | "}
          Last response:{" "}
          <strong style={{ color: UI.text }}>
            {ageSeconds === null ? "—" : `${ageSeconds}s ago`}
          </strong>
          {" | "}
          Responses: <strong style={{ color: UI.text }}>{stats.responses}</strong>
          {" | "}
          Timeouts: <strong style={{ color: UI.text }}>{stats.timeouts}</strong>
        </div>
      </div>

      {scanSecondsLeft > 0 && (
        <p style={{ marginTop: 8, color: UI.subtleText }}>
          ⏳ Transport scan… <strong style={{ color: UI.text }}>{scanSecondsLeft}s</strong>{" "}
          remaining
        </p>
      )}

      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          onClick={connect}
          disabled={status === "scanning" || status === "connected"}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: `2px solid ${UI.buttonBorder}`,
            background: UI.buttonBg,
            color: UI.text,
            cursor: "pointer",
            fontWeight: 800,
          }}
        >
          Connect
        </button>

        <button
          onClick={dumpGattToLogs}
          disabled={!isConnected()}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: `2px solid ${UI.buttonBorder}`,
            background: UI.buttonBg,
            color: UI.text,
            cursor: "pointer",
            fontWeight: 800,
          }}
        >
          Dump GATT to Logs
        </button>
      </div>

      <div
        style={{
          marginTop: 14,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 14,
        }}
      >
        <Gauge270 {...gaugeTheme.speedMph} value={parsed.speedMph} />
        <Gauge270 {...gaugeTheme.rpm} value={parsed.rpm} />
        <Gauge270 {...gaugeTheme.coolantF} value={parsed.coolantF} />
        <Gauge270 {...gaugeTheme.intakeF} value={parsed.intakeF} />
        <Gauge270 {...gaugeTheme.throttlePct} value={parsed.throttlePct} />
        <Gauge270 {...gaugeTheme.engineLoadPct} value={parsed.engineLoadPct} />
        <Gauge270 {...gaugeTheme.fuelPct} value={parsed.fuelPct} />
        <Gauge270 {...gaugeTheme.mapKpa} value={parsed.mapKpa} />
      </div>

      <div
        style={{
          marginTop: 18,
          padding: 12,
          border: `2px solid ${UI.border}`,
          borderRadius: 14,
          background: UI.panelBg2,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 8, color: UI.text }}>
          ✅ Good responses (latest per PID)
        </div>
        {Object.keys(goodByPid).length === 0 ? (
          <div style={{ color: UI.subtleText }}>
            No “41xx” good responses captured yet.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 8,
            }}
          >
            {Object.entries(goodByPid).map(([pid, resp]) => (
              <div key={pid} style={{ fontFamily: "monospace", fontSize: 12, color: UI.text }}>
                <strong>{pid}</strong> {resp}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 18,
          padding: 12,
          border: `2px solid ${UI.border}`,
          borderRadius: 14,
          background: UI.panelBg2,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 6, color: UI.text }}>
          Supported Mode 01 PIDs (we will poll) — {supportedMode01Pids.length}
        </div>
        {supportedMode01Pids.length === 0 ? (
          <div style={{ color: UI.subtleText }}>—</div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 8,
            }}
          >
            {supportedMode01Pids.map((p) => (
              <div
                key={p.pidHex}
                style={{
                  padding: 10,
                  border: `2px solid ${UI.borderSoft}`,
                  borderRadius: 12,
                  background: UI.panelBg,
                }}
              >
                <div style={{ fontWeight: 900, color: UI.text }}>{p.name}</div>
                <div style={{ fontFamily: "monospace", fontSize: 12, color: UI.text }}>
                  {p.pidHex}
                </div>
                <div style={{ fontSize: 12, color: UI.subtleText }}>{p.unit}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          height: 360,
          overflowY: "auto",
          border: `2px solid ${UI.border}`,
          marginTop: 16,
          padding: 12,
          background: UI.panelBg,
          borderRadius: 14,
        }}
      >
        <div style={{ fontWeight: 900, marginBottom: 8, color: UI.text }}>
          Logs
        </div>
        {logLines.map((line, i) => (
          <div
            key={i}
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              whiteSpace: "pre-wrap",
              color: UI.text,
            }}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}